import React, { useState, useEffect } from 'react'
import {
  Table,
  Tag,
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Drawer,
  Alert,
  Checkbox,
  Progress,
  Typography,
  Collapse,
} from 'antd'
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  SyncOutlined,
  MailOutlined,
  StopOutlined,
  ClearOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { leadsApi, Lead, CreateLeadData, UpdateLeadData, LeadStats, userApi, AdminUser } from '../services/api'
import LeadDetail from '../components/LeadDetail'
import ConvertLeadToBusiness from '../components/ConvertLeadToBusiness'

const { Search } = Input
const { Option } = Select
const { TextArea } = Input

type SfQueueJobRow = {
  id: string
  type: string
  state: string
  attemptsMade: number
  timestamp: number
  failedReason: string | null
  runId?: string
}

/** Always available even if API presets endpoint is down / not deployed yet. */
const DEFAULT_TRADES = [
  'HVAC',
  'plumbing',
  'electrical',
  'roofing',
  'landscaping',
  'pest control',
  'garage door',
  'cleaning',
  'remodeling',
  'painting',
]

const DEFAULT_GEOS = [
  'Dallas, TX',
  'Houston, TX',
  'Austin, TX',
  'San Antonio, TX',
  'Fort Worth, TX',
  'Little Rock, AR',
  'Phoenix, AZ',
  'Tucson, AZ',
  'Los Angeles, CA',
  'San Diego, CA',
  'San Francisco, CA',
  'Sacramento, CA',
  'Denver, CO',
  'Colorado Springs, CO',
  'Atlanta, GA',
  'Miami, FL',
  'Orlando, FL',
  'Tampa, FL',
  'Jacksonville, FL',
  'Chicago, IL',
  'Indianapolis, IN',
  'New Orleans, LA',
  'Boston, MA',
  'Detroit, MI',
  'Minneapolis, MN',
  'Kansas City, MO',
  'St. Louis, MO',
  'Charlotte, NC',
  'Raleigh, NC',
  'Las Vegas, NV',
  'New York, NY',
  'Columbus, OH',
  'Oklahoma City, OK',
  'Portland, OR',
  'Philadelphia, PA',
  'Nashville, TN',
  'Memphis, TN',
  'Salt Lake City, UT',
  'Seattle, WA',
  'Milwaukee, WI',
]

const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([])
  const [stats, setStats] = useState<LeadStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    assignedTo: '',
    city: '',
    state: '',
    createdWithin: '' as '' | 'today' | '7d' | '30d',
    fromMaps: '' as '' | 'true' | 'false',
    emailQuality: '' as '' | 'real' | 'placeholder',
    source: '',
  })
  const [searchInput, setSearchInput] = useState('')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [convertModalVisible, setConvertModalVisible] = useState(false)
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [discoverModalVisible, setDiscoverModalVisible] = useState(false)
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [discoverForm] = Form.useForm()
  const [exportForm] = Form.useForm()
  const [presetTrades, setPresetTrades] = useState<string[]>(DEFAULT_TRADES)
  const [presetGeos, setPresetGeos] = useState<string[]>(DEFAULT_GEOS)
  const [discoveryBusy, setDiscoveryBusy] = useState(false)
  const [discoveryPanelKeys, setDiscoveryPanelKeys] = useState<string[]>([])
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([])
  const [csvDownloading, setCsvDownloading] = useState(false)
  const [workerStatus, setWorkerStatus] = useState<{
    workerHint: string
    workerLikelyOnline: boolean
    deployTask: string
    maxPerQueryLimit?: number
    queue: {
      redisConfigured: boolean
      counts: Record<string, number> | null
      error?: string
    }
    jobs?: SfQueueJobRow[]
    recentRuns: Array<{
      id: string
      status: string
      stats?: Record<string, number | string | null> | null
      error?: string | null
      createdAt: string
      updatedAt?: string
      trades?: unknown
      geos?: unknown
      maxPerQuery?: number
    }>
  } | null>(null)

  useEffect(() => {
    fetchLeads()
    fetchStats()
    fetchAdminUsers()
  }, [pagination.current, pagination.pageSize, filters])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim()
      setFilters((prev) => {
        if (prev.search === next) return prev
        setPagination((p) => ({ ...p, current: 1 }))
        return { ...prev, search: next }
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    leadsApi.getSfDiscoveryPresets()
      .then((res) => {
        const trades = res.data.trades?.length ? res.data.trades : DEFAULT_TRADES
        const geos = res.data.geos?.length ? res.data.geos : DEFAULT_GEOS
        setPresetTrades(trades)
        setPresetGeos(geos)
      })
      .catch(() => {
        setPresetTrades(DEFAULT_TRADES)
        setPresetGeos(DEFAULT_GEOS)
      })
    void refreshWorkerStatus()
  }, [])

  useEffect(() => {
    const hasWork =
      (workerStatus?.queue.counts?.waiting ?? 0) > 0 ||
      (workerStatus?.queue.counts?.active ?? 0) > 0 ||
      workerStatus?.recentRuns?.some((r) => r.status === 'pending' || r.status === 'running')
    if (!hasWork) return
    const timer = window.setInterval(() => {
      void refreshWorkerStatus()
    }, 4000)
    return () => window.clearInterval(timer)
  }, [
    workerStatus?.queue.counts?.waiting,
    workerStatus?.queue.counts?.active,
    workerStatus?.recentRuns,
  ])

  useEffect(() => {
    const waiting = workerStatus?.queue.counts?.waiting ?? 0
    const active = workerStatus?.queue.counts?.active ?? 0
    const running = workerStatus?.recentRuns?.some(
      (r) => r.status === 'pending' || r.status === 'running'
    )
    if (waiting > 0 || active > 0 || running) {
      setDiscoveryPanelKeys((prev) => (prev.includes('queue') ? prev : [...prev, 'queue']))
    }
  }, [
    workerStatus?.queue.counts?.waiting,
    workerStatus?.queue.counts?.active,
    workerStatus?.recentRuns,
  ])

  const refreshWorkerStatus = async () => {
    try {
      const res = await leadsApi.getSfWorkerStatus()
      setWorkerStatus(res.data)
    } catch {
      setWorkerStatus(null)
    }
  }

  const openDiscoverModal = () => {
    const trades = (presetTrades.length ? presetTrades : DEFAULT_TRADES).slice(0, 1)
    const geos = presetGeos.length ? [...presetGeos] : [...DEFAULT_GEOS]
    discoverForm.setFieldsValue({
      trades,
      geos,
      maxPerQuery: 60,
    })
    setDiscoverModalVisible(true)
  }

  const handleDiscover = async (values: {
    trades: string[]
    geos: string[]
    maxPerQuery?: number
  }) => {
    setDiscoveryBusy(true)
    try {
      const started = await leadsApi.startSfDiscoveryRun({
        trades: values.trades,
        geos: values.geos,
        maxPerQuery: values.maxPerQuery,
      })
      message.success(`Discovery queued (${started.data.runId.slice(0, 8)}…)`)
      setDiscoverModalVisible(false)
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to start discovery')
    } finally {
      setDiscoveryBusy(false)
    }
  }

  const handleRefreshStale = async () => {
    try {
      const res = await leadsApi.startSfRefresh({ limit: 100 })
      if (res.data.queueStatus === 'already_queued') {
        message.info(`Refresh already ${res.data.state || 'queued'} — not duplicated`)
      } else {
        message.success('Stale Places refresh queued')
      }
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to refresh leads')
    }
  }

  const handleEnrichEmails = async () => {
    try {
      const res = await leadsApi.startSfEnrich({ limit: 100 })
      if (res.data.queueStatus === 'already_queued') {
        message.info(`Enrich already ${res.data.state || 'queued'} — not duplicated`)
      } else {
        message.success('Email enrichment queued')
      }
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to enrich leads')
    }
  }

  const handleCancelQueueJob = async (jobId: string) => {
    try {
      await leadsApi.cancelSfQueueJob(jobId)
      message.success('Job cancelled')
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to cancel job')
    }
  }

  const handleRetryQueueJob = async (jobId: string) => {
    try {
      await leadsApi.retrySfQueueJob(jobId)
      message.success('Job re-queued')
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to retry job')
    }
  }

  const handleClearWaiting = async () => {
    try {
      const res = await leadsApi.clearSfWaitingJobs()
      message.success(`Cleared ${res.data.removed} waiting job(s)`)
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to clear waiting jobs')
    }
  }

  const handleCancelDiscoveryRun = async (runId: string) => {
    try {
      await leadsApi.cancelSfDiscoveryRun(runId)
      message.success('Discovery run cancelled')
      void refreshWorkerStatus()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to cancel run')
    }
  }

  const handleExportRangeLeads = async (values: {
    workspaceId: string
    listName?: string
    consentAttested: boolean
  }) => {
    try {
      const result = await leadsApi.exportSfToRangeLeads({
        workspaceId: values.workspaceId.trim(),
        listName: values.listName,
        consentAttested: Boolean(values.consentAttested),
      })
      message.success(`Exported ${result.data.imported} contacts to RangeLeads`)
      setExportModalVisible(false)
      exportForm.resetFields()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Export failed')
    }
  }

  const csvEscape = (value: unknown): string => {
    const raw = value == null ? '' : String(value)
    if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`
    return raw
  }

  const buildLeadsCsv = (rows: Lead[]): string => {
    const header = [
      'Company',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Zip',
      'Website',
      'Status',
      'Priority',
      'Source',
      'Lead Source',
      'Service Area',
      'Google Rating',
      'Google Reviews',
      'Google Place ID',
      'Additional Contacts',
      'Created At',
    ]

    const lines = [
      header.join(','),
      ...rows.map((lead) => {
        const extraContacts = (lead.contacts || [])
          .map((c) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
            return name ? `${name} <${c.email}>` : c.email
          })
          .join('; ')
        return [
          csvEscape(lead.company),
          csvEscape(lead.firstName),
          csvEscape(lead.lastName),
          csvEscape(lead.email),
          csvEscape(lead.phone),
          csvEscape(lead.address),
          csvEscape(lead.city),
          csvEscape(lead.state),
          csvEscape(lead.zipCode),
          csvEscape(lead.websiteUrl),
          csvEscape(lead.status),
          csvEscape(lead.priority),
          csvEscape(lead.source),
          csvEscape(lead.leadSource),
          csvEscape(lead.serviceArea),
          csvEscape(lead.googleStarRating),
          csvEscape(lead.googleReviewCount),
          csvEscape(lead.googlePlaceId),
          csvEscape(extraContacts),
          csvEscape(lead.createdAt ? new Date(lead.createdAt).toISOString() : ''),
        ].join(',')
      }),
    ]
    return lines.join('\n')
  }

  const downloadCsvBlob = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const listFilterParams = () => ({
    search: filters.search || undefined,
    status: filters.status || undefined,
    priority: filters.priority || undefined,
    assignedTo: filters.assignedTo || undefined,
    city: filters.city || undefined,
    state: filters.state || undefined,
    createdWithin: filters.createdWithin || undefined,
    fromMaps:
      filters.fromMaps === 'true' ? true : filters.fromMaps === 'false' ? false : undefined,
    emailQuality: filters.emailQuality || undefined,
    source: filters.source || undefined,
    sortBy: 'createdAt' as const,
    sortOrder: 'desc' as const,
  })

  const fetchAllFilteredLeads = async (): Promise<Lead[]> => {
    const pageSize = 100
    const base = listFilterParams()
    const first = await leadsApi.list({ ...base, page: 1, limit: pageSize })
    const total = first.pagination?.total ?? first.data.length
    const all = [...(first.data || [])]
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    for (let page = 2; page <= totalPages; page++) {
      const res = await leadsApi.list({ ...base, page, limit: pageSize })
      all.push(...(res.data || []))
    }
    return all
  }

  const handleDownloadCsv = async () => {
    setCsvDownloading(true)
    try {
      let rows: Lead[] = []

      if (selectedLeadIds.length) {
        const detailed = await Promise.all(
          selectedLeadIds.map(async (id) => {
            const fromPage = leads.find((l) => l.id === id)
            try {
              const res = await leadsApi.get(id)
              return res.data
            } catch {
              return fromPage || null
            }
          })
        )
        rows = detailed.filter(Boolean) as Lead[]
      } else {
        rows = await fetchAllFilteredLeads()
      }

      if (!rows.length) {
        message.warning('No leads to download')
        return
      }

      downloadCsvBlob(buildLeadsCsv(rows), `sf-leads-${new Date().toISOString().slice(0, 10)}.csv`)
      message.success(
        selectedLeadIds.length
          ? `Downloaded ${rows.length} selected lead(s)`
          : `Downloaded ${rows.length} lead(s)`
      )
    } catch (error: any) {
      message.error(error.response?.data?.message || 'CSV download failed')
    } finally {
      setCsvDownloading(false)
    }
  }

  const fetchLeads = async () => {
    setLoading(true)
    try {
      const response = await leadsApi.list({
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        assignedTo: filters.assignedTo || undefined,
        city: filters.city || undefined,
        state: filters.state || undefined,
        createdWithin: filters.createdWithin || undefined,
        fromMaps:
          filters.fromMaps === 'true' ? true : filters.fromMaps === 'false' ? false : undefined,
        emailQuality: filters.emailQuality || undefined,
        source: filters.source || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      })
      setLeads(response.data)
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }))
    } catch (error: any) {
      console.error('Failed to fetch leads:', error)
      message.error('Failed to load leads')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await leadsApi.getStats()
      setStats(response.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const fetchAdminUsers = async () => {
    try {
      const users = await userApi.list()
      setAdminUsers(users)
    } catch (error) {
      console.error('Failed to fetch admin users:', error)
    }
  }

  const handleSearch = (value: string) => {
    setSearchInput(value)
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || '' }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const clearAllFilters = () => {
    setSearchInput('')
    setFilters({
      search: '',
      status: '',
      priority: '',
      assignedTo: '',
      city: '',
      state: '',
      createdWithin: '',
      fromMaps: '',
      emailQuality: '',
      source: '',
    })
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const applyQuickFilter = (chip: 'new_today' | 'status_new' | 'from_maps') => {
    setPagination((prev) => ({ ...prev, current: 1 }))
    if (chip === 'new_today') {
      setFilters((prev) => ({
        ...prev,
        status: 'new',
        createdWithin: prev.createdWithin === 'today' && prev.status === 'new' ? '' : 'today',
      }))
      return
    }
    if (chip === 'status_new') {
      setFilters((prev) => ({
        ...prev,
        status: prev.status === 'new' ? '' : 'new',
      }))
      return
    }
    setFilters((prev) => ({
      ...prev,
      fromMaps: prev.fromMaps === 'true' ? '' : 'true',
    }))
  }

  const handleTableChange = (paginationConfig: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }))
  }

  const handleViewLead = async (lead: Lead) => {
    try {
      const response = await leadsApi.get(lead.id)
      setSelectedLead(response.data)
      setDetailDrawerVisible(true)
    } catch (error) {
      console.error('Failed to fetch lead details:', error)
      message.error('Failed to load lead details')
    }
  }

  const handleCreateLead = async (values: CreateLeadData) => {
    try {
      await leadsApi.create(values)
      message.success('Lead created successfully')
      setCreateModalVisible(false)
      createForm.resetFields()
      fetchLeads()
      fetchStats()
    } catch (error: any) {
      console.error('Failed to create lead:', error)
      message.error(error.response?.data?.message || 'Failed to create lead')
    }
  }

  const handleEditLead = async (values: UpdateLeadData) => {
    if (!selectedLead) return
    try {
      await leadsApi.update(selectedLead.id, values)
      message.success('Lead updated successfully')
      setEditModalVisible(false)
      editForm.resetFields()
      fetchLeads()
      if (detailDrawerVisible) {
        const response = await leadsApi.get(selectedLead.id)
        setSelectedLead(response.data)
      }
    } catch (error: any) {
      console.error('Failed to update lead:', error)
      message.error(error.response?.data?.message || 'Failed to update lead')
    }
  }

  const handleDeleteLead = async (leadId: string) => {
    try {
      await leadsApi.delete(leadId)
      message.success('Lead deleted successfully')
      fetchLeads()
      fetchStats()
      if (selectedLead?.id === leadId) {
        setDetailDrawerVisible(false)
        setSelectedLead(null)
      }
    } catch (error: any) {
      console.error('Failed to delete lead:', error)
      message.error(error.response?.data?.message || 'Failed to delete lead')
    }
  }

  const handleAssignLead = async (leadId: string, userId?: string) => {
    try {
      await leadsApi.assign(leadId, userId)
      message.success('Lead assigned successfully')
      fetchLeads()
      if (selectedLead?.id === leadId) {
        const response = await leadsApi.get(leadId)
        setSelectedLead(response.data)
      }
    } catch (error: any) {
      console.error('Failed to assign lead:', error)
      message.error(error.response?.data?.message || 'Failed to assign lead')
    }
  }

  const handleConvertToBusiness = async (businessData: any) => {
    if (!selectedLead) return
    try {
      await leadsApi.convertToBusiness(selectedLead.id, businessData)
      message.success('Lead converted to business successfully')
      setConvertModalVisible(false)
      fetchLeads()
      fetchStats()
      if (detailDrawerVisible) {
        const response = await leadsApi.get(selectedLead.id)
        setSelectedLead(response.data)
      }
    } catch (error: any) {
      console.error('Failed to convert lead:', error)
      message.error(error.response?.data?.message || 'Failed to convert lead to business')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'blue',
      contacted: 'cyan',
      qualified: 'green',
      nurturing: 'purple',
      demo_scheduled: 'orange',
      onboarding: 'gold',
      converted: 'success',
      lost: 'red'
    }
    return colors[status] || 'default'
  }

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'green',
      medium: 'blue',
      high: 'orange',
      urgent: 'red'
    }
    return colors[priority] || 'default'
  }

  const displayName = (lead: Lead) => {
    const company = lead.company?.trim()
    if (company) return company
    const person = `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
    return person || 'N/A'
  }

  const isPlaceholderEmail = (email: string | null | undefined) => {
    if (!email) return true
    const e = email.toLowerCase()
    return (
      e.includes('@placeholder.') ||
      e.includes('@sf-lead.placeholder.') ||
      e.startsWith('noemail')
    )
  }

  const columns = [
    {
      title: 'Company / Contact',
      key: 'contact',
      ellipsis: true,
      render: (_: unknown, record: Lead) => {
        const name = displayName(record)
        const email = record.email || ''
        const placeholder = isPlaceholderEmail(email)
        return (
          <div style={{ minWidth: 0, maxWidth: '100%' }}>
            <Tooltip title={name}>
              <div
                style={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </div>
            </Tooltip>
            <Tooltip title={placeholder ? 'No real email yet' : email}>
              <div
                style={{
                  fontSize: 12,
                  color: placeholder ? '#999' : '#666',
                  fontStyle: placeholder ? 'italic' : 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {placeholder ? 'No email' : email}
              </div>
            </Tooltip>
            {record.phone && (
              <div
                style={{
                  fontSize: 12,
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.phone}
              </div>
            )}
            <Space size={4} wrap style={{ marginTop: 4 }}>
              {record.googlePlaceId ? <Tag style={{ margin: 0 }}>Maps</Tag> : null}
              {typeof record.contactCount === 'number' && record.contactCount > 0 ? (
                <Tag
                  color="blue"
                  style={{ margin: 0, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleViewLead(record)
                  }}
                >
                  {record.contactCount} contact{record.contactCount === 1 ? '' : 's'}
                </Tag>
              ) : null}
              {placeholder ? (
                <Tag color="default" style={{ margin: 0 }}>
                  Needs email
                </Tag>
              ) : null}
            </Space>
          </div>
        )
      },
    },
    {
      title: 'Location',
      key: 'location',
      width: 140,
      ellipsis: true,
      render: (_: unknown, record: Lead) => {
        const loc = [record.city, record.state].filter(Boolean).join(', ')
        return loc ? (
          <Tooltip title={[record.address, loc, record.zipCode].filter(Boolean).join(' · ')}>
            <span style={{ fontSize: 12 }}>{loc}</span>
          </Tooltip>
        ) : (
          <span style={{ color: '#999', fontSize: 12 }}>—</span>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
    },
    {
      title: 'Assigned',
      key: 'assignedTo',
      width: 120,
      ellipsis: true,
      render: (_: unknown, record: Lead) =>
        record.assignedUser ? (
          <Tooltip title={`${record.assignedUser.firstName} ${record.assignedUser.lastName}`}>
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.assignedUser.firstName} {record.assignedUser.lastName}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>Unassigned</span>
        ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => {
        const d = new Date(date)
        const isToday = d.toDateString() === new Date().toDateString()
        return (
          <Tooltip title={d.toLocaleString()}>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap', fontWeight: isToday ? 600 : 400 }}>
              {isToday ? 'Today · ' : ''}
              {d.toLocaleDateString()}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: unknown, record: Lead) => (
        <Space size={0}>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewLead(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedLead(record)
                editForm.setFieldsValue({
                  firstName: record.firstName,
                  lastName: record.lastName,
                  email: record.email,
                  phone: record.phone,
                  company: record.company,
                  address: record.address,
                  city: record.city,
                  state: record.state,
                  zipCode: record.zipCode,
                  message: record.message,
                  status: record.status,
                  priority: record.priority,
                  notes: record.notes,
                  serviceArea: record.serviceArea,
                  websiteUrl: record.websiteUrl,
                  googleStarRating: record.googleStarRating,
                  googleReviewCount: record.googleReviewCount
                })
                setEditModalVisible(true)
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this lead?"
            onConfirm={() => handleDeleteLead(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24, maxWidth: '100%', overflowX: 'hidden' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1>Business Leads</h1>
        <p>Manage potential ServiceFlow business leads and nurture them until onboarding</p>
      </div>

      {/* Map discovery panel moved below leads table */}

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Leads"
                value={stats.total}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Conversion Rate"
                value={stats.conversionRate.toFixed(1)}
                suffix="%"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Recent Activity"
                value={stats.recentActivity}
                suffix="(30 days)"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="New Leads"
                value={stats.byStatus.new || 0}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Button
            size="small"
            type={filters.status === 'new' && filters.createdWithin === 'today' ? 'primary' : 'default'}
            onClick={() => applyQuickFilter('new_today')}
          >
            New today
          </Button>
          <Button
            size="small"
            type={filters.status === 'new' && filters.createdWithin !== 'today' ? 'primary' : 'default'}
            onClick={() => applyQuickFilter('status_new')}
          >
            Status: New
          </Button>
          <Button
            size="small"
            type={filters.fromMaps === 'true' ? 'primary' : 'default'}
            onClick={() => applyQuickFilter('from_maps')}
          >
            From Maps
          </Button>
        </Space>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Search
              placeholder="Search company, email, city, contacts…"
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Input
              placeholder="City"
              allowClear
              value={filters.city || undefined}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={3} lg={2}>
            <Input
              placeholder="State"
              allowClear
              maxLength={2}
              value={filters.state || undefined}
              onChange={(e) => handleFilterChange('state', e.target.value.toUpperCase())}
            />
          </Col>
          <Col xs={12} sm={8} md={5} lg={3}>
            <Select
              placeholder="Created"
              value={filters.createdWithin || undefined}
              onChange={(value) => handleFilterChange('createdWithin', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="today">Today</Option>
              <Option value="7d">Last 7 days</Option>
              <Option value="30d">Last 30 days</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={5} lg={3}>
            <Select
              placeholder="From Maps"
              value={filters.fromMaps || undefined}
              onChange={(value) => handleFilterChange('fromMaps', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="true">Maps only</Option>
              <Option value="false">Not from Maps</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={5} lg={3}>
            <Select
              placeholder="Email"
              value={filters.emailQuality || undefined}
              onChange={(value) => handleFilterChange('emailQuality', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="real">Has real email</Option>
              <Option value="placeholder">Needs email</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Select
              placeholder="Status"
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="new">New</Option>
              <Option value="contacted">Contacted</Option>
              <Option value="qualified">Qualified</Option>
              <Option value="nurturing">Nurturing</Option>
              <Option value="demo_scheduled">Demo Scheduled</Option>
              <Option value="onboarding">Onboarding</Option>
              <Option value="converted">Converted</Option>
              <Option value="lost">Lost</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={4} lg={2}>
            <Select
              placeholder="Priority"
              value={filters.priority || undefined}
              onChange={(value) => handleFilterChange('priority', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3}>
            <Select
              placeholder="Assigned To"
              value={filters.assignedTo || undefined}
              onChange={(value) => handleFilterChange('assignedTo', value || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="">Unassigned</Option>
              {adminUsers.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button icon={<EnvironmentOutlined />} onClick={openDiscoverModal}>
                Discover from Maps
              </Button>
              <Button icon={<SyncOutlined />} onClick={handleRefreshStale}>
                Refresh stale
              </Button>
              <Button icon={<MailOutlined />} onClick={handleEnrichEmails}>
                Enrich emails
              </Button>
              <Button icon={<ExportOutlined />} onClick={() => setExportModalVisible(true)}>
                Export to RangeLeads
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={csvDownloading}
                onClick={() => void handleDownloadCsv()}
              >
                Download CSV{selectedLeadIds.length ? ` (${selectedLeadIds.length})` : ''}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchLeads()
                  fetchStats()
                }}
              >
                Refresh
              </Button>
              <Button icon={<FilterOutlined />} onClick={clearAllFilters}>
                Clear Filters
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                Create Lead
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Leads Table */}
      <Card
        styles={{ body: { overflowX: 'auto' } }}
        title={selectedLeadIds.length ? `${selectedLeadIds.length} selected` : undefined}
        extra={
          selectedLeadIds.length ? (
            <Space>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={csvDownloading}
                onClick={() => void handleDownloadCsv()}
              >
                Download CSV
              </Button>
              <Button size="small" onClick={() => setSelectedLeadIds([])}>
                Clear selection
              </Button>
            </Space>
          ) : null
        }
      >
        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: 720 }}
          rowSelection={{
            selectedRowKeys: selectedLeadIds,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setSelectedLeadIds(keys as string[]),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 20
              }))
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} leads`
          }}
        />
      </Card>

      <Card
        id="map-discovery"
        size="small"
        style={{ marginTop: 16, marginBottom: 16 }}
        title="Map pack discovery"
        extra={
          <Space size="small">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {workerStatus?.queue.counts
                ? `wait ${workerStatus.queue.counts.waiting ?? 0} · active ${workerStatus.queue.counts.active ?? 0}`
                : workerStatus?.workerHint || '…'}
            </Typography.Text>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => void refreshWorkerStatus()}>
              Status
            </Button>
          </Space>
        }
      >
        <Alert
          type={
            workerStatus?.queue.redisConfigured
              ? workerStatus.workerLikelyOnline
                ? 'success'
                : 'warning'
              : 'error'
          }
          showIcon
          style={{ marginBottom: 12 }}
          message={workerStatus?.workerHint || 'Loading worker status…'}
          description="Searches only the metros you pick (not every city in a state). Google Places returns up to ~60 results per trade × metro."
        />
        <Collapse
          activeKey={discoveryPanelKeys}
          onChange={(keys) => setDiscoveryPanelKeys(Array.isArray(keys) ? keys : [keys])}
          items={[
            {
              key: 'queue',
              label: `Queue & discovery runs (${workerStatus?.jobs?.length || 0} jobs)`,
              children: (
                <div>
                  {workerStatus?.jobs && workerStatus.jobs.length > 0 ? (
                    <Table<SfQueueJobRow>
                      size="small"
                      pagination={{ pageSize: 5, size: 'small' }}
                      rowKey="id"
                      dataSource={workerStatus.jobs}
                      style={{ marginBottom: 16 }}
                      columns={[
                        {
                          title: 'Type',
                          dataIndex: 'type',
                          width: 120,
                          render: (t) => String(t || '').replace(/^sf_lead_/, ''),
                        },
                        {
                          title: 'State',
                          dataIndex: 'state',
                          width: 100,
                          render: (s: string) => (
                            <Tag
                              color={
                                s === 'active'
                                  ? 'processing'
                                  : s === 'waiting' || s === 'delayed'
                                    ? 'gold'
                                    : s === 'failed'
                                      ? 'red'
                                      : s === 'completed'
                                        ? 'green'
                                        : 'default'
                              }
                            >
                              {s}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Job',
                          dataIndex: 'id',
                          ellipsis: true,
                          render: (id: string, row) => (
                            <code style={{ fontSize: 12 }}>{String(id).slice(0, 18)}</code>
                          ),
                        },
                        {
                          title: 'Queued',
                          dataIndex: 'timestamp',
                          width: 150,
                          render: (ts: number) => (ts ? new Date(ts).toLocaleString() : '—'),
                        },
                        {
                          title: '',
                          key: 'actions',
                          width: 140,
                          render: (_value, row) => (
                            <Space size={4}>
                              {(row.state === 'waiting' ||
                                row.state === 'delayed' ||
                                row.state === 'active' ||
                                row.state === 'paused') && (
                                <Button
                                  size="small"
                                  danger
                                  icon={<StopOutlined />}
                                  onClick={() => void handleCancelQueueJob(row.id)}
                                >
                                  Stop
                                </Button>
                              )}
                              {(row.state === 'failed' || row.state === 'completed') && (
                                <Button
                                  size="small"
                                  icon={<PlayCircleOutlined />}
                                  onClick={() => void handleRetryQueueJob(row.id)}
                                >
                                  Retry
                                </Button>
                              )}
                            </Space>
                          ),
                        },
                      ]}
                    />
                  ) : (
                    <Typography.Text type="secondary">No queue jobs</Typography.Text>
                  )}

                  {workerStatus?.recentRuns?.length ? (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Recent discovery runs</div>
                      {workerStatus.recentRuns.map((run) => {
                        const runStats = run.stats || {}
                        const queries = Number(runStats.queries || 0)
                        const queriesTotal = Number(runStats.queriesTotal || 0)
                        const percent =
                          typeof runStats.percent === 'number'
                            ? Number(runStats.percent)
                            : queriesTotal > 0
                              ? Math.round((queries / queriesTotal) * 100)
                              : run.status === 'completed'
                                ? 100
                                : 0
                        const canCancel = run.status === 'pending' || run.status === 'running'
                        return (
                          <div
                            key={run.id}
                            style={{
                              fontSize: 13,
                              marginBottom: 12,
                              paddingBottom: 8,
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            <Space wrap style={{ marginBottom: 4 }}>
                              <Tag
                                color={
                                  run.status === 'completed'
                                    ? 'green'
                                    : run.status === 'failed'
                                      ? 'red'
                                      : run.status === 'cancelled'
                                        ? 'default'
                                        : run.status === 'running'
                                          ? 'processing'
                                          : 'blue'
                                }
                              >
                                {run.status}
                              </Tag>
                              <span>{new Date(run.createdAt).toLocaleString()}</span>
                              <code style={{ fontSize: 12 }}>{run.id.slice(0, 8)}…</code>
                              {canCancel && (
                                <Button
                                  size="small"
                                  danger
                                  icon={<StopOutlined />}
                                  onClick={() => void handleCancelDiscoveryRun(run.id)}
                                >
                                  Cancel run
                                </Button>
                              )}
                            </Space>
                            {(run.status === 'running' ||
                              run.status === 'pending' ||
                              queriesTotal > 0) && (
                              <div style={{ maxWidth: 480 }}>
                                <Progress
                                  percent={percent}
                                  size="small"
                                  status={
                                    run.status === 'failed'
                                      ? 'exception'
                                      : run.status === 'completed'
                                        ? 'success'
                                        : 'active'
                                  }
                                />
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  {runStats.currentTrade && runStats.currentGeo
                                    ? `${runStats.currentTrade} × ${runStats.currentGeo}`
                                    : null}
                                  {queriesTotal ? ` · query ${queries}/${queriesTotal}` : null}
                                  {` · created ${runStats.created ?? 0} · updated ${runStats.updated ?? 0} · with email ${runStats.withEmail ?? 0} · errors ${runStats.errors ?? 0}`}
                                </Typography.Text>
                              </div>
                            )}
                            {run.error && (
                              <div style={{ color: '#cf1322', fontSize: 12 }}>{run.error}</div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Create Lead Modal */}
      <Modal
        title="Create New Lead"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          createForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateLead}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name">
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name">
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="phone" label="Phone">
            <Input placeholder="Phone number" />
          </Form.Item>

          <Form.Item name="company" label="Company">
            <Input placeholder="Company name" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input placeholder="Street address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="State">
                <Input placeholder="State" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="zipCode" label="ZIP Code">
                <Input placeholder="ZIP Code" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="message" label="Message/Notes">
            <TextArea rows={4} placeholder="Additional information" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="new">
                <Select>
                  <Option value="new">New</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="qualified">Qualified</Option>
                  <Option value="nurturing">Nurturing</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="serviceArea" label="Service Area">
            <Input placeholder="Service area / region" />
          </Form.Item>

          <Form.Item name="websiteUrl" label="Website URL">
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="googleStarRating" label="Google Star Rating">
                <InputNumber min={0} max={5} step={0.1} placeholder="e.g., 4.5" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="googleReviewCount" label="Google Review Count">
                <InputNumber min={0} placeholder="e.g., 150" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Internal Notes">
            <TextArea rows={3} placeholder="Internal notes (not visible to lead)" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false)
                createForm.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Lead
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal
        title="Edit Lead"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          editForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditLead}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name">
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name">
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="phone" label="Phone">
            <Input placeholder="Phone number" />
          </Form.Item>

          <Form.Item name="company" label="Company">
            <Input placeholder="Company name" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input placeholder="Street address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="State">
                <Input placeholder="State" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="zipCode" label="ZIP Code">
                <Input placeholder="ZIP Code" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="message" label="Message/Notes">
            <TextArea rows={4} placeholder="Additional information" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="new">New</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="qualified">Qualified</Option>
                  <Option value="nurturing">Nurturing</Option>
                  <Option value="demo_scheduled">Demo Scheduled</Option>
                  <Option value="onboarding">Onboarding</Option>
                  <Option value="converted">Converted</Option>
                  <Option value="lost">Lost</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="serviceArea" label="Service Area">
            <Input placeholder="Service area / region" />
          </Form.Item>

          <Form.Item name="websiteUrl" label="Website URL">
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="googleStarRating" label="Google Star Rating">
                <InputNumber min={0} max={5} step={0.1} placeholder="e.g., 4.5" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="googleReviewCount" label="Google Review Count">
                <InputNumber min={0} placeholder="e.g., 150" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Internal Notes">
            <TextArea rows={3} placeholder="Internal notes" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false)
                editForm.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Lead
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Lead Detail Drawer */}
      <Drawer
        title="Lead Details"
        placement="right"
        width={800}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false)
          setSelectedLead(null)
        }}
      >
        {selectedLead && (
          <LeadDetail
            lead={selectedLead}
            adminUsers={adminUsers}
            onUpdate={() => {
              fetchLeads()
              fetchStats()
            }}
            onAssign={(userId) => handleAssignLead(selectedLead.id, userId)}
            onConvert={() => setConvertModalVisible(true)}
          />
        )}
      </Drawer>

      {/* Convert to Business Modal */}
      {selectedLead && (
        <ConvertLeadToBusiness
          visible={convertModalVisible}
          lead={selectedLead}
          onCancel={() => setConvertModalVisible(false)}
          onComplete={handleConvertToBusiness}
        />
      )}

      <Modal
        title="Discover SF leads from Google Places"
        open={discoverModalVisible}
        onCancel={() => setDiscoverModalVisible(false)}
        footer={null}
        width={640}
      >
        <Form form={discoverForm} layout="vertical" onFinish={handleDiscover}>
          <Form.Item
            name="trades"
            label="Trades"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one trade' }]}
            extra="Click the field to pick from the list, or type a custom trade and press Enter"
          >
            <Select
              mode="tags"
              allowClear
              showSearch
              placeholder="HVAC, plumbing, …"
              tokenSeparators={[',']}
              optionFilterProp="label"
              notFoundContent="Type a trade and press Enter"
              options={DEFAULT_TRADES.map((t) => ({ value: t, label: t }))}
            />
          </Form.Item>
          <Form.Item
            name="geos"
            label="Cities / metros"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one city' }]}
            extra="Only these metros are searched — not every city in each state. Type any metro (e.g. Boise, ID) and press Enter to add."
          >
            <Select
              mode="tags"
              allowClear
              showSearch
              placeholder="Dallas, TX"
              tokenSeparators={[';', ',']}
              optionFilterProp="label"
              notFoundContent="Type a city and press Enter"
              options={DEFAULT_GEOS.map((g) => ({ value: g, label: g }))}
            />
          </Form.Item>
          <Form.Item
            name="maxPerQuery"
            label="Target with email per metro"
            extra="Aim for this many leads with a real email per trade × metro (default 60). We search alternate phrases and prefer businesses with websites; Hunter + site scrape fill emails. Not every metro can hit 60 if Google/Hunter lack data."
          >
            <InputNumber min={1} max={200} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setDiscoverModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={discoveryBusy}>
                Start discovery
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Export to RangeLeads"
        open={exportModalVisible}
        onCancel={() => {
          setExportModalVisible(false)
          exportForm.resetFields()
        }}
        footer={null}
        width={520}
      >
        <Form form={exportForm} layout="vertical" onFinish={handleExportRangeLeads}>
          <Form.Item
            name="workspaceId"
            label="RangeLeads workspace ID"
            rules={[{ required: true, message: 'Workspace ID is required' }]}
          >
            <Input placeholder="UUID of the RangeLeads workspace" />
          </Form.Item>
          <Form.Item name="listName" label="List name" initialValue="ServiceFlow Admin Leads">
            <Input />
          </Form.Item>
          <Form.Item
            name="consentAttested"
            valuePropName="checked"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value) throw new Error('Consent attestation is required')
                },
              },
            ]}
          >
            <Checkbox>
              I attest these contacts may be emailed under our outreach policy
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  setExportModalVisible(false)
                  exportForm.resetFields()
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Export
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default LeadsPage

